import React, {FC, useEffect, useState} from "react";
import {
    useFindUserSettingsByGroupNameQuery,
    useUpdateUserSettingsMutation,
} from "../userSetting/userSettingsApi";
import {modifyPageSizeSetting} from "../userSetting/UserSettingUtils";
import {UserSetting} from "../userSetting/UserSettingTypes";
import {FaArrowRight, FaArrowLeft, FaCog} from "react-icons/fa";
import IconButton from "../form/IconButton";
import ModalButton from "../form/ModalButton";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";
import UserSettingsModal from "../userSetting/UserSettingsModal";
import {skipToken} from "@reduxjs/toolkit/query";

interface PaginationProps {
    displayText: string;
    pageCount: number;
    pageNumber: number;
    setPageNumber: (pageNumber: number) => void;
    groupName?: any;
    setExternalPageSize: (pageSize: number) => void;
    pagesAfterEllipsis?: number;
    isHoa?: boolean;
    props?: any;
    permissions?:any
}

const Pagination: FC<PaginationProps> = ({
                                             displayText,
                                             pageCount,
                                             pageNumber,
                                             setPageNumber,
                                             groupName,
                                             setExternalPageSize,
                                             pagesAfterEllipsis = 3,
                                             isHoa = false,
                                             props,
                                             permissions="UPRO-R"
                                         }) => {
    const userSettingsQuery = useFindUserSettingsByGroupNameQuery(groupName || skipToken);
    const [updateUserSettings] = useUpdateUserSettingsMutation();

    const [pageSize, setPageSize] = useState("15");


    useEffect(() => {
        const data = userSettingsQuery.data;

        if (userSettingsQuery.isSuccess && Array.isArray(data)) {
            const initialValue = getPageSizeSettingValue(data);
            if (initialValue) {
                setPageSize(initialValue.toString());
            }
        }
    }, [userSettingsQuery.isSuccess, userSettingsQuery.data]);

    useEffect(() => {
        setExternalPageSize(parseInt(pageSize, 10));
    }, [pageSize, setExternalPageSize]);

    function getPageSizeSettingValue(settings?: UserSetting[]) {
        return (
            settings?.find(
                (s) => s.group === groupName && s.key === "pageSize"
            )?.value || "15"
        );
    }

    function changePage(page: number) {
        setPageNumber(page);
    }

    async function changePerPageSize(size: number) {
        if (userSettingsQuery?.data) {
            await updateUserSettings(
                modifyPageSizeSetting(groupName, userSettingsQuery?.data, size)
            );
            // Don't set pageSize here — let useEffect handle it after query refreshes
        }
    }


    useEffect(() => {
        if (Array.isArray(userSettingsQuery?.data)) {
            const updatedPageSize = getPageSizeSettingValue(userSettingsQuery.data);
            if (updatedPageSize) {
                setPageSize(updatedPageSize.toString());
                setExternalPageSize(parseInt(updatedPageSize, 10));
            }
        } else {
            console.warn("Invalid user settings data:", userSettingsQuery?.data);
        }
    }, [userSettingsQuery?.data]);

    function renderPageNumbers() {

        const startPage = pageCount > 6 ? Math.max(0, pageNumber - pagesAfterEllipsis) : 0;
        const endPage = pageCount > 6 ? Math.min(pageCount, pageNumber + pagesAfterEllipsis + 1) : pageCount;

        return [...Array(pageCount).keys()]
            .slice(startPage, endPage)
            .map((num) => (
                <button
                    type={"button"}
                    key={num}
                    onClick={() => changePage(num)}
                    aria-current={num === pageNumber ? "page" : undefined}
                    className={`min-w-[30px] h-[30px] flex justify-center items-center rounded-full text-surface-0 shadow-custom ${num === pageNumber
                        ? !isHoa ? `bg-highlight-0 text-text-0` : `bg-highlight-1 text-text-0`
                        : !isHoa ? `hover:bg-highlight-0 hover:text-text-0` : `hover:bg-highlight-1 hover:text-text-0` 
                    }`}
                >
                    <span className={"text-xs font-bold"}>  {num + 1}</span>

                </button>
            ));
    }

    return (
        <div className="flex items-center justify-between my-3 px-3 w-auto">
            <div className="space-x-2 flex items-center">
                {displayText && (
                    <span className="text-surface-0 pr-2 text-sm">
                        {pageCount > 0 ? displayText : ""}
                    </span>
                )}

                <button
                    type={"button"}
                    onClick={() => changePage(pageNumber - 1)}
                    disabled={pageNumber === 0}
                    aria-label="Previous Page"
                    className={`min-w-[30px] h-[30px] flex justify-center items-center rounded-full text-surface-0 shadow-custom ${pageNumber === 0
                        ? "cursor-not-allowed"
                        : "hover:text-highlight-4 "
                    }`}
                >
                    <FaArrowLeft size={14}/>
                </button>

                {pageCount > pagesAfterEllipsis + 3 && pageNumber > pagesAfterEllipsis && (
                    <>
                        <button
                            type={"button"}
                            onClick={() => changePage(0)}
                            className="min-w-[30px] h-[30px] flex justify-center items-center rounded-full shadow-custom"
                        >
                            1
                        </button>
                        {pageNumber > pagesAfterEllipsis + 1 && (
                            <span className="text-surface-0 text-xs">...</span>
                        )}
                    </>
                )}

                {renderPageNumbers()}

                {pageCount > pagesAfterEllipsis + 3 &&
                    pageNumber < pageCount - pagesAfterEllipsis - 1 && (
                        <>
                            {pageNumber < pageCount - pagesAfterEllipsis - 2 && (
                                <span className="text-surface-0 text-xs">...</span>
                            )}
                            <button
                                type={"button"}
                                onClick={() => changePage(pageCount - 1)}
                                className="min-w-[30px] h-[30px] flex justify-center items-center rounded-full shadow-custom"
                            >
                                {pageCount}
                            </button>
                        </>
                    )}

                <button
                    type={"button"}
                    onClick={() => changePage(pageNumber + 1)}
                    disabled={pageNumber + 1 >= pageCount}
                    aria-label="Next Page"
                    className={`min-w-[30px] h-[30px] flex justify-center items-center rounded-full text-surface-0 shadow-custom ${pageNumber + 1 >= pageCount
                        ? !isHoa ? `bg-highlight-0 text-text-0` : `bg-highlight-1 text-text-0`
                        : !isHoa ? `hover:bg-highlight-0 hover:text-text-0` : `hover:bg-highlight-1 hover:text-text-0`
                    }`}
                >
                    <FaArrowRight size={14}/>
                </button>
            </div>
            {userSettingsQuery?.data && (
                <div className="ml-4 flex items-center space-x-2">
                    {hasPermission([permissions]) && <ModalButton
                        modal={<UserSettingsModal
                            columns={props?.columns}
                            show={true}
                            setShow={() => {
                            }}
                            headerGroups={props?.columns.filter((cur: any) => (
                                cur.visibility.defaultHidden === false ||
                                (cur.visibility.defaultHidden === true && (cur.visibility.canBeHidden === true || cur.visibility.canBeHidden === false))
                            ))}
                            groupName={props?.userSettingGroup || ''}

                        />
                        }
                        triggerButton={
                            <IconButton
                                icon={<FaCog size={24} className="shrink-0"/>}
                                title={props?.userSettingGroup + " Settings"}
                                className="text-highlight-0 mr-8"
                            />
                        }/>}
                    <span className="display_text pr-2 text-sm">Results per page</span>
                    {[15, 25, 50, 100, 150].map((size) => (
                        <button
                            type={"button"}
                            key={size}
                            onClick={() => changePerPageSize(size)}
                            className={`min-w-[30px] h-[30px] flex justify-center items-center rounded-full shadow-custom ${parseInt(pageSize, 10) === size
                                ? !isHoa ? `bg-highlight-0 text-text-0` : `bg-highlight-1 text-text-0`
                                : !isHoa ? `hover:bg-highlight-0 hover:text-text-0` : `hover:text-text-0`
                            }`}
                        >
                            <span className="text-xs font-bold">{size}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Pagination;
